import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class LocationMappingService {
  private readonly STORAGE_KEY = 'nict_location_slug';
  
  // Map URL slugs to full location names
  private locationMap: { [key: string]: string } = {
    'hnm': 'Hanumanthanagar',
    'hanumanthanagar': 'Hanumanthanagar',
    'jayanagar': 'Jayanagar',
    'jnr': 'Jayanagar',
    'domlur': 'Domlur',
    'dml': 'Domlur',
    'channasandra': 'Channasandra',
    'chn': 'Channasandra',
    'koramangala': 'Kormangala',
    'kormangala': 'Kormangala',
    'kmg': 'Kormangala',
    'yelachenahalli': 'Yelachenahalli',
    'yln': 'Yelachenahalli',
    'malleshwaram': 'Malleshwaram',
    'mlw': 'Malleshwaram',
    'kothanur': 'Kothanur',
    'kth': 'Kothanur',
    'kammanahalli': 'Kammanahalli',
    'kmh': 'Kammanahalli',
    'uttarahalli': 'Uttarahalli',
    'uth': 'Uttarahalli',
    'chandra-layout': 'Chandra Layout',
    'chandralayout': 'Chandra Layout',
    'chl': 'Chandra Layout',
    'sr-nagar': 'S R Nagar',
    'srnagar': 'S R Nagar',
    'srn': 'S R Nagar'
  };

  constructor() { }

  /**
   * Get full location name from URL slug
   * @param slug - URL slug (e.g., 'hnm', 'jayanagar')
   * @returns Full location name or null if not found
   */
  getLocationFromSlug(slug: string): string | null {
    if (!slug) return null;
    
    const normalizedSlug = slug.toLowerCase().trim();
    return this.locationMap[normalizedSlug] || null;
  }

  /**
   * Check if a slug is valid
   * @param slug - URL slug to validate
   * @returns true if slug exists in mapping
   */
  isValidSlug(slug: string): boolean {
    if (!slug) return false;
    const normalizedSlug = slug.toLowerCase().trim();
    return normalizedSlug in this.locationMap;
  }

  /**
   * Get all available location slugs
   * @returns Array of valid slugs
   */
  getAvailableSlugs(): string[] {
    return Object.keys(this.locationMap);
  }

  /**
   * Store location slug in localStorage
   * @param slug - URL slug to store
   */
  storeLocationSlug(slug: string): void {
    if (!slug) return;
    
    const normalizedSlug = slug.toLowerCase().trim();
    if (this.isValidSlug(normalizedSlug)) {
      try {
        localStorage.setItem(this.STORAGE_KEY, normalizedSlug);
      } catch (e) {
        console.warn('Failed to store location slug in localStorage:', e);
      }
    }
  }

  /**
   * Get stored location slug from localStorage
   * @returns Stored slug or null if not found
   */
  getStoredLocationSlug(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to retrieve location slug from localStorage:', e);
      return null;
    }
  }

  /**
   * Get full location name from stored slug
   * @returns Full location name or null if not found
   */
  getStoredLocation(): string | null {
    const storedSlug = this.getStoredLocationSlug();
    if (storedSlug) {
      return this.getLocationFromSlug(storedSlug);
    }
    return null;
  }

  /**
   * Clear stored location slug from localStorage
   */
  clearStoredLocation(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear location slug from localStorage:', e);
    }
  }

  /**
   * Get location from slug and automatically store it
   * @param slug - URL slug to process
   * @returns Full location name or null if not found
   */
  getLocationAndStore(slug: string): string | null {
    const location = this.getLocationFromSlug(slug);
    if (location) {
      this.storeLocationSlug(slug);
    }
    return location;
  }
}
